// Review Data Model
export const reviewModel = {
  // Review Collection Structure
  collection: 'reviews',
  
  // Default Review Document Structure
  defaultDocument: {
    id: '', // Auto-generated by Firestore
    type: '', // customer_to_girl, girl_to_customer, product, store
    bookingId: '', // Reference to booking (for booking reviews)
    productId: '', // Reference to product (for product reviews)
    storeId: '', // Reference to store (for store reviews)
    orderId: '', // Reference to order (for product/store reviews)
    reviewerId: '', // User who wrote the review
    reviewerName: '', // Reviewer's display name
    reviewerRole: '', // customer, girl, buyer, store_owner, etc.
    revieweeId: '', // User being reviewed (for user reviews)
    revieweeName: '', // Reviewee's display name
    revieweeRole: '', // customer, girl
    
    // Review Content
    rating: 5, // 1-5 stars
    title: '', // Review title
    comment: '', // Review text
    tags: [], // Array of tags like ['friendly', 'punctual', 'professional']
    
    // Review Status
    status: 'pending', // pending, approved, rejected, hidden
    isPublic: true, // Whether review is visible to others
    
    // Metadata
    createdAt: '', // ISO string
    updatedAt: '', // ISO string
    reviewedAt: '', // When the actual service was provided
    
    // Response (for reviewee/seller to respond)
    response: {
      comment: '',
      respondedAt: '',
      respondedBy: ''
    },
    
    // Moderation
    moderatedBy: '', // Admin who moderated
    moderatedAt: '', // When it was moderated
    moderationReason: '' // Reason for rejection/hiding
  },
  
  // Review Types
  types: {
    CUSTOMER_TO_GIRL: 'customer_to_girl',
    GIRL_TO_CUSTOMER: 'girl_to_customer',
    PRODUCT: 'product', // รีวิวสินค้า
    STORE: 'store' // รีวิวร้านค้า
  },
  
  // Review Status
  status: {
    PENDING: 'pending',
    APPROVED: 'approved',
    REJECTED: 'rejected',
    HIDDEN: 'hidden'
  },
  
  // Rating Scale
  rating: {
    MIN: 1,
    MAX: 5,
    DEFAULT: 5
  },
  
  // Common Review Tags
  tags: {
    // For Girls
    GIRL_TAGS: [
      'friendly', // เป็นมิตร
      'professional', // เป็นมืออาชีพ
      'punctual', // ตรงเวลา
      'attractive', // สวยงาม
      'good_service', // บริการดี
      'clean', // สะอาด
      'fun', // สนุก
      'caring', // ใส่ใจ
      'communicative', // สื่อสารดี
      'reliable' // น่าเชื่อถือ
    ],
    
    // For Customers
    CUSTOMER_TAGS: [
      'respectful', // ให้เกียรติ
      'polite', // สุภาพ
      'punctual', // ตรงเวลา
      'generous', // ใจกว้าง
      'clean', // สะอาด
      'fun', // สนุก
      'communicative', // สื่อสารดี
      'reliable', // น่าเชื่อถือ
      'understanding', // เข้าใจ
      'appreciative' // รู้คุณค่า
    ],
    
    // For Products
    PRODUCT_TAGS: [
      'high_quality', // คุณภาพดี
      'fresh', // สดใหม่
      'good_value', // คุ้มค่า
      'accurate_description', // ตรงตามคำอธิบาย
      'fast_delivery', // ส่งเร็ว
      'well_packaged', // บรรจุภัณฑ์ดี
      'as_advertised', // ตรงตามโฆษณา
      'excellent', // ยอดเยี่ยม
      'satisfied', // พอใจ
      'recommended' // แนะนำ
    ],
    
    // For Stores
    STORE_TAGS: [
      'reliable', // น่าเชื่อถือ
      'fast_response', // ตอบกลับเร็ว
      'good_service', // บริการดี
      'professional', // เป็นมืออาชีพ
      'trustworthy', // เชื่อถือได้
      'helpful', // ช่วยเหลือดี
      'good_communication', // สื่อสารดี
      'satisfied', // พอใจ
      'recommended', // แนะนำ
      'excellent' // ยอดเยี่ยม
    ]
  },
  
  // Validation Rules
  validation: {
    rating: {
      required: true,
      min: 1,
      max: 5,
      type: 'number'
    },
    title: {
      required: true,
      minLength: 5,
      maxLength: 100,
      type: 'string'
    },
    comment: {
      required: true,
      minLength: 5,
      maxLength: 1000,
      type: 'string'
    },
    tags: {
      required: false,
      maxItems: 5,
      type: 'array'
    }
  },
  
  // Review Statistics
  statistics: {
    // Calculate average rating
    calculateAverageRating: (reviews) => {
      if (!reviews || reviews.length === 0) return 0;
      const totalRating = reviews.reduce((sum, review) => sum + review.rating, 0);
      return Math.round((totalRating / reviews.length) * 10) / 10;
    },
    
    // Calculate rating distribution
    calculateRatingDistribution: (reviews) => {
      const distribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      reviews.forEach(review => {
        distribution[review.rating]++;
      });
      return distribution;
    },
    
    // Get most common tags
    getMostCommonTags: (reviews, limit = 5) => {
      const tagCount = {};
      reviews.forEach(review => {
        review.tags?.forEach(tag => {
          tagCount[tag] = (tagCount[tag] || 0) + 1;
        });
      });
      
      return Object.entries(tagCount)
        .sort(([,a], [,b]) => b - a)
        .slice(0, limit)
        .map(([tag, count]) => ({ tag, count }));
    }
  }
};

// Helper Functions
export const reviewUtils = {
  // Check if user can review
  canUserReview: (userRole, bookingStatus) => {
    // Only customers and girls can review
    if (!['customer', 'girl'].includes(userRole)) return false;
    
    // Only completed bookings can be reviewed
    if (bookingStatus !== 'completed') return false;
    
    return true;
  },
  
  // Check if user can review product
  canReviewProduct: (userRole, orderStatus) => {
    // Only buyers can review products
    if (!['buyer', 'store_owner', 'customer'].includes(userRole)) return false;
    
    // Only delivered orders can be reviewed
    if (orderStatus !== 'delivered') return false;
    
    return true;
  },
  
  // Check if user can review store
  canReviewStore: (userRole, orderStatus) => {
    // Only buyers can review stores
    if (!['buyer', 'store_owner', 'customer'].includes(userRole)) return false;
    
    // Only delivered orders can be reviewed
    if (orderStatus !== 'delivered') return false;
    
    return true;
  },
  
  // Check if review is editable
  isReviewEditable: (review, currentUserId) => {
    // Only reviewer can edit
    if (review.reviewerId !== currentUserId) return false;
    
    // Only pending reviews can be edited
    if (review.status !== 'pending') return false;
    
    // Can't edit after 7 days
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const reviewDate = new Date(review.createdAt);
    
    return reviewDate > sevenDaysAgo;
  },
  
  // Check if review can be responded to
  canRespondToReview: (review, currentUserId) => {
    // Only reviewee can respond
    if (review.revieweeId !== currentUserId) return false;
    
    // Only approved reviews can be responded to
    if (review.status !== 'approved') return false;
    
    // Can't respond if already responded
    if (review.response?.comment) return false;
    
    return true;
  },
  
  // Format rating for display
  formatRating: (rating) => {
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  },
  
  // Get rating text
  getRatingText: (rating) => {
    const ratingTexts = {
      1: 'แย่มาก',
      2: 'แย่',
      3: 'ปานกลาง',
      4: 'ดี',
      5: 'ดีมาก'
    };
    return ratingTexts[rating] || 'ไม่ระบุ';
  }
};

export default reviewModel;
